import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Slider,
  Alert,
  CircularProgress
} from "@mui/material";

import { getProjects, getProject, updateProject, getEmployeeCapacity } from "../../services/projectService";

function AssignProjectDialog({ open, employee, onClose, onAssigned }) {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [capacityPercent, setCapacityPercent] = useState(0);
  const [availableCapacity, setAvailableCapacity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedProjectId("");
    setCapacityPercent(0);
    setAvailableCapacity(null);
    setError("");

    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const data = await getProjects();
        setProjects(data);
      } catch (err) {
        console.error("Error loading projects:", err);
        setError("Failed to load projects");
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [open]);

  useEffect(() => {
    const loadAvailableCapacity = async () => {
      if (!selectedProjectId || !employee) {
        setAvailableCapacity(null);
        return;
      }

      try {
        const project = projects.find((p) => p.project_id === selectedProjectId);

        const capacity = await getEmployeeCapacity(
          employee.employee_id,
          selectedProjectId,
          project?.start_date || null,
          project?.end_date || null
        );

        setAvailableCapacity(capacity.available_capacity_percent);
      } catch (err) {
        console.error("Error loading employee capacity:", err);
        setAvailableCapacity(null);
      }
    };

    loadAvailableCapacity();
  }, [selectedProjectId, employee, projects]);

  const handleAssign = async () => {
    if (!selectedProjectId || !employee) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const project = await getProject(selectedProjectId);

      const mergedAssignedTo = [
        ...project.assigned_to.filter((a) => a.employee_id !== employee.employee_id),
        { employee_id: employee.employee_id, capacity_percent: capacityPercent }
      ];

      await updateProject(selectedProjectId, {
        project_name: project.project_name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        assigned_to: mergedAssignedTo
      });

      onAssigned?.();
      onClose();
    } catch (err) {
      console.error("Error assigning project:", err);
      setError(err?.response?.data?.detail || "Failed to assign project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Assign Project{employee ? ` to ${employee.employee_name}` : ""}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Project"
            value={selectedProjectId}
            disabled={loadingProjects}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            {projects.map((project) => (
              <MenuItem key={project.project_id} value={project.project_id}>
                {project.project_name}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155", mb: 0.5 }}>
              Capacity Allocation: {capacityPercent}%
            </Typography>

            <Slider
              value={capacityPercent}
              onChange={(e, value) => setCapacityPercent(value)}
              step={5}
              min={0}
              max={100}
              valueLabelDisplay="auto"
            />

            {availableCapacity !== null && (
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                Available capacity for this employee: {availableCapacity}%
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedProjectId || saving}
        >
          {saving ? <CircularProgress size={20} /> : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignProjectDialog;
